import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '../baseQuery';
import { onboardingApi } from '../onboarding/onboardingApi';
import { HaveFinish } from 'utils/finishes';

export interface SetDetail {
  name: string;           // Set name (e.g., "Mirage")
  code: string;           // Set code (e.g., "mir")
  cardId: number;         // Card ID for this printing
  collectorNumber?: string; // Collector number (e.g., "235", "466")
  imageUrl?: string;      // Full CDN URL for card image
  scryfallId?: string;    // Scryfall ID for CDN URL construction
  availableInFoil?: boolean;      // Whether this specific printing exists in foil (foil-or-etched through Track D)
  availableInNonFoil?: boolean;   // Whether this specific printing exists in non-foil
  /** Finishes this printing exists in (lowercase, from the backend helper). Three-value controls read this. */
  availableFinishes?: string[];
}

export interface TradeCard {
  id: string;
  cardId: number;
  name: string;
  set: string;
  setCode: string;
  condition: string;
  language: string;
  /** Derived mirror of `finish` (finish !== 'NONFOIL'); kept in sync by finishFields() */
  foil: boolean;
  /** Authoritative finish of the copy being sent (Track D) */
  finish?: HaveFinish;
  edition: string;
  imageUrl: string;
  marketPrice: number;
  pointValue: number;
  foilPointValue?: number;
  nonFoilPointValue?: number;
  etchedPointValue?: number | null;
  quantity: number;
  wantListId: number;
  acceptableSets?: string[];  // Sets the receiver will accept
  availableInFoil?: boolean;  // Whether this card is available in foil
  availableInNonFoil?: boolean;  // Whether this card is available in non-foil
  // New fields for hover tooltip
  acceptableSetDetails?: SetDetail[];  // Detailed info for each acceptable printing
  acceptableLanguages?: string[];      // Languages receiver accepts (omitted = any)
  acceptableConditions?: string[];     // Conditions receiver accepts (omitted = any)
  acceptFoil?: boolean;                // Receiver accepts foil
  acceptNonFoil?: boolean;             // Receiver accepts non-foil
  acceptEtched?: boolean | null;       // Receiver accepts etched (Track D; absent follows acceptFoil)
  availableFinishes?: string[];        // Finishes the displayed printing exists in (lowercase)
  scryfallId?: string;                 // Scryfall ID for the displayed card
  availableLanguages?: Array<{ lang: string; scryfallId: string; setCode?: string }>;  // Language variants for this printing
  /** When the receiver added this want (ISO). Orders a collapsed list newest first; absent on an older backend. */
  wantCreatedAt?: string;
}

export interface ShippingAddress {
  firstName?: string;
  lastName?: string;
  address1?: string;
  address2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
}

export interface TradePackage {
  userId: number;
  username: string;
  avatarUrl?: string;
  pointBalance: number;
  cards: TradeCard[];
  totalValue: number;
  country?: string;  // Country for flag display (not PII)
  shippingAddress?: ShippingAddress;
  isLocked?: boolean;
  lockedBy?: string;
  lockExpiresAt?: string;
  isElite?: boolean;
  showAnimatedBorder?: boolean;
  borderColor1?: string | null;
  borderColor2?: string | null;
}

export interface AvailablePackagesResponse {
  packages: TradePackage[];
  totalPackages: number;
  hasMore: boolean;
  page: number;
  limit: number;
}

export interface AvailablePackagesRequest {
  page?: number;
  limit?: number;
  search?: string;
  searchType?: 'card' | 'user';  // Search mode: 'card' = card names only, 'user' = usernames only
  priceMin?: number;  // Minimum card price filter (in points)
  priceMax?: number;  // Maximum card price filter (in points)
  forExport?: boolean;  // Raises backend limit cap from 50 to 500
  // Server-side auto-match filtering: the backend derives the match set itself
  // (TradeController maps this to TradeService's useMatchIndex). Replaces the
  // old client-enumerated `userIds` list, whose URL grew with the user base.
  autoMatch?: boolean;
  sort?: string;  // Sort field: 'value' | 'cards' | 'newest'
  direction?: 'asc' | 'desc';  // Sort direction
  setCodes?: string;  // Comma-separated set codes for set filtering
}

// Surface a trade was initiated from, for telemetry (trade_initiation_events). Sent as an
// optional body field; backend records 'unknown' if missing/unrecognized.
export type TradeInitiationSource = 'send-cards' | 'profile' | 'trade-alerts';

export interface InitiateTradeRequest {
  receiverId: number;
  lockToken?: string;  // Lock token required by backend
  source?: TradeInitiationSource; // telemetry: which surface initiated this trade
  cards: {
    wantListId: number;
    cardId?: string;  // Specific card ID for exact version matching
    quantity: number;
    condition: string;
    language: string;
    /** Authoritative (Track D); sent with the derived `foil` through D */
    finish?: HaveFinish;
    foil: boolean;
    edition?: string;
    set?: string;  // The specific set the sender is sending
  }[];
}

export interface InitiateTradeResponse {
  tradeId: string;
  success: boolean;
  message?: string;
}

export interface ActiveTrade {
  id: string;
  tradeNumber: string;
  traderId: number;
  traderName: string;
  traderAvatar?: string;
  traderIsElite?: boolean;
  traderShowAnimatedBorder?: boolean;
  traderBorderColor1?: string | null;
  traderBorderColor2?: string | null;
  pointBalance?: number;
  initiated: string;
  shipped?: string;
  completed?: string;
  estimatedDelivery?: string;
  // 'pending_funding': a trade-offer leg waiting for its funding leg to
  // complete before it opens. Buckets into the ready/preparing tab
  // server-side; the fields below explain what it waits for.
  status: 'ready' | 'transit' | 'completed' | 'cancelled' | 'pending_funding';
  awaitingFunding?: boolean;
  fundedByTradeId?: string;
  fundedByTradeNumber?: string;
  // What the leg's receiver had set aside at accept (their net on the offer);
  // the rest of the escrow is taken when the funding trade pays them. 0 on
  // every other trade, absent on a backend that predates it. Not zeroed when
  // the leg opens, so read it only while awaitingFunding.
  reservedPoints?: number;
  items: {
    /** TradeItem UUID (Asana 1219952262403623); the Summon Admin wizard reports affected cards by it. */
    id: string;
    qty: number;
    name: string;
    set: string;
    setCode?: string;
    setIconUrl?: string;
    collectorNumber?: string;
    condition: string;
    language: string;
    foil: boolean;
    /** Track D; null on legacy rows (derive from foil via finishFromRow) */
    finish?: HaveFinish | null;
    image: string;
    scryfallId?: string;
    pointValue?: number;
    // Set (ISO timestamp) when this card was individually admin-completed and the sender credited.
    completedAt?: string | null;
  }[];
  totalValue: number;
  // Actionable remainder = totalValue - sum of completed (non-removed) items. Equals totalValue when nothing is completed.
  pendingValue?: number;
  // Points, at the live rate, with any exemption applied (server-computed).
  tradeFee?: number;
  // True when this trade pays no fee: a trade-offer leg, or an Elite sender.
  // The same predicate the server uses for tradeFee. Never infer exemption from
  // tradeFee === 0 (fees round down, so a small trade's fee is 0 too). Optional
  // until the backend that sends it is deployed.
  feeExempt?: boolean;
  cancelled?: string;
  shippingAddress?: ShippingAddress;
  // Rating fields (populated from TradeRating if exists)
  speed?: number;
  accuracy?: number;
  communication?: number;
  // Present when this entry represents an individually removed item
  isRemovedItem?: boolean;
  removedFromTradeNumber?: string;
  // Packing slip access window (7 days after completion)
  packingSlipAvailable?: boolean;
  packingSlipExpiresAt?: string | null;
  // Coarse location of the party who is not the viewer. The only geography the
  // panel map may read; never derive a location from shippingAddress.
  counterpartyRegion?: TradeRegion;
  // Start of the 72h shipping window: openedAt ?? initiatedAt, the anchor the
  // backend stale sweeper uses. Sent on every /trades/active row; null while
  // awaitingFunding and on removed-item rows. Required: every ship-window
  // surface reads it and nothing falls back to initiated.
  shipWindowStart: string | null;
}

/** State and country only, by contract. country is ISO alpha-2 upper; state is a 2-3 letter code. */
export interface TradeRegion {
  state: string | null;
  country: string | null;
}

export interface TransitEstimate {
  status: 'pending' | 'in_transit' | 'arriving_soon' | 'delayed' | 'delivered' | 'cancelled';
  label: string;
  minDays: number;
  maxDays: number;
  elapsedDays?: number;
  // Stepped display value (caps at 90, then 90/95/98), NOT linear. The panel
  // rows compute their own linear progress from shipped + estimatedDeliveryBy.
  progressPercent?: number;
  transitType?: 'domestic' | 'international' | 'unknown';
}

export interface TradePanelRow {
  id: string;
  tradeNumber: string;
  // pending_funding is folded into 'ready' with awaitingFunding: true
  status: 'ready' | 'transit' | 'completed';
  awaitingFunding: boolean;
  // On the wire for parity with ActiveTrade; the panel does not render it.
  reservedPoints?: number;
  traderId: number;
  traderName: string;
  traderAvatar?: string;
  traderIsElite?: boolean;
  traderShowAnimatedBorder?: boolean;
  traderBorderColor1?: string | null;
  traderBorderColor2?: string | null;
  points: number;
  cardCount: number;
  initiated: string;
  shipWindowStart: string | null;
  // Null on a completed row is real and common: a receiver can complete a
  // trade straight from READY, and shippedAt is only written by markAsShipped.
  shipped: string | null;
  completed: string | null;
  // shipped + transitEstimate.maxDays; transit rows only
  estimatedDeliveryBy: string | null;
  transitEstimate?: TransitEstimate;
  counterpartyRegion: TradeRegion;
}

export interface TradePanelTile {
  // Trade UUID, or `removed-<itemId>` for an individually removed item
  id: string;
  tradeNumber: string;
  traderName: string;
  points: number;
  outcome: 'completed' | 'cancelled';
  isRemovedItem: boolean;
  at: string;
  counterpartyRegion: TradeRegion;
  // The tile's lead card (highest value item; the removed item on a removed-item
  // row), as the same `image` / `scryfallId` values the list rows carry in
  // items[]. Optional: a backend that predates them gets the card-back fallback.
  image?: string | null;
  // CDN small size of the same card. The tiles stopped reading it when they grew
  // to 100x140 (design review round 2) and load `image`, the normal size (about
  // 488x680), instead. Still on the wire.
  imageSmall?: string | null;
  scryfallId?: string | null;
  cardName?: string | null;
}

// One row per distinct counterparty over ALL of the viewer's completed trades in
// the viewed direction, filtered by `search` exactly as stats.byStatus is, so
// sum(tradeCount) equals byStatus.completed.tradeCount. Never capped: the See all
// modal has to reach every one-off partner. Backend order: tradeCount desc,
// lastCompletedAt desc nulls last, userId asc.
export interface TradePanelPartner {
  userId: number;
  username: string;
  avatarUrl: string | null;
  // The User.isElite column, the same source the public profile reads
  isElite: boolean;
  showAnimatedBorder: boolean;
  borderColor1: string | null;
  borderColor2: string | null;
  // False only for a deleted account (username `deleted_user_<id>`): the row
  // still renders, but not as a link to a profile that 404s
  profileAvailable: boolean;
  // The partner's CURRENT profile region, not a trade-time snapshot. Never a
  // null object; state can be null while country is set.
  region: TradeRegion;
  tradeCount: number;
  // Median, not mean, of window-open to delivered in days, 1 decimal. Null only
  // when no trade in the group has a completed timestamp.
  medianDeliveryDays: number | null;
  // SUM of the trades' locked totalValue in integer points; never recomputed.
  // NOT byStatus.completed.points, which subtracts individually completed items.
  pointsExchanged: number;
  lastCompletedAt: string | null;
}

export interface ActiveTradesPanelRequest {
  type: 'sending' | 'receiving';
  search?: string;
}

// Set-scoped for the user+type: independent of the active tab and page, and
// filtered by `search` exactly as stats.byStatus is, so badges and panel agree.
export interface ActiveTradesPanelResponse {
  viewerRegion: TradeRegion;
  // Ready rows first (window start asc), then transit rows (shipped asc); 100 per bucket
  live: TradePanelRow[];
  liveReadyTotal: number;
  liveTransitTotal: number;
  // 100 most recent, newest first. Unread by the frontend since the partner map
  // replaced the flight-path chart; still on the wire.
  completed: TradePanelRow[];
  completedTotal: number;
  // The Completed tab's partner map, rail and modal. Absent on a backend that
  // predates it (deploy order is backend first).
  partners?: TradePanelPartner[];
  // Completed + cancelled merged, newest first, cap 60
  tiles: TradePanelTile[];
  tilesTotal: number;
  cancelledTiles: TradePanelTile[];
  cancelledTilesTotal: number;
  // Last 20 completed, IGNORES search. sampleSize is how many of those had
  // `shipped` set, so it is often under 20; min/max are null when it is 0.
  history: {
    transitDaysMin: number | null;
    transitDaysMax: number | null;
    sampleSize: number;
    regions: Array<TradeRegion & { count: number }>;
  };
  // Copies of the viewer's for-trade cards some active wanter accepts. From the
  // SendableCard precompute (up to 15 min stale); null when type=receiving.
  sendableCardCount: number | null;
}

export interface ActiveTradesRequest {
  type: 'sending' | 'receiving';
  status: 'ready' | 'transit' | 'completed' | 'cancelled';
  page: number;
  limit: number;
  search?: string;
}

export interface TradesPagination {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

export interface ActiveTradesStats {
  statusCounts: {
    ready: number;
    transit: number;
    completed: number;
    cancelled: number;
    pending_funding?: number;
  };
  // Global for the user+type, ignores status and search. Sending: transit-only
  // sum; receiving: sum over all entries. Backend replicates the legacy header math.
  totalPoints: number;
  // Per-tab aggregates over the whole filtered set. Unlike statusCounts these
  // RESPECT search, and tradeCount equals pagination.totalItems for the same
  // type+status+search (ready includes pending_funding; cancelled counts rows,
  // removed-item rows included). Absent on a backend that predates it.
  byStatus?: Record<ActiveTradesRequest['status'], TradeStatusAggregate>;
}

export interface TradeStatusAggregate {
  tradeCount: number;
  points: number;
  cardCount: number;
}

export interface ActiveTradesResponse {
  trades: ActiveTrade[];
  pagination: TradesPagination;
  stats: ActiveTradesStats;
}

export interface TradeLockRequest {
  receiverId: number;
}

export interface TradeLockResponse {
  success: boolean;
  message?: string;
  lockId?: string;
  lockToken?: string;  // Token to validate lock ownership
  expiresAt?: string;
}

export interface TradeUnlockRequest {
  receiverId: number;
  lockToken: string;
}

export interface MarkAsShippedRequest {
  tradeId: string;
}

export interface MarkAsShippedResponse {
  success: boolean;
  message?: string;
  shippedAt?: string;
}

export interface RemoveCardFromTradeRequest {
  tradeId: string;
  itemIndex: number;  // The index of the item to remove from the trade
}

export interface RemoveCardFromTradeResponse {
  success: boolean;
  message?: string;
  updatedTrade?: ActiveTrade;
  refundedPoints?: number;
}

export interface CompleteTradeRequest {
  tradeId: string;
}

export interface CompleteTradeResponse {
  success: boolean;
  message?: string;
  completedAt?: string;
  pointsCredited?: number;
  tradeFee?: number;
  tradeFeePercentage?: number;
  isFirstTrade?: boolean;
  shareImageUrl?: string;
}

export interface GenerateShareImageRequest {
  tradeId: string;
}

export interface GenerateShareImageResponse {
  shareImageUrl: string;
}

export interface AdminTrade {
  id: string;
  tradeNumber: string;
  senderId: number;
  senderUsername: string;
  senderAvatar?: string;
  receiverId: number;
  receiverUsername: string;
  receiverAvatar?: string;
  totalValue: number;
  initiatedAt: string;
  shippedAt?: string;
  completedAt?: string;
  status: 'ready' | 'transit' | 'completed' | 'cancelled';
  items: AdminTradeItem[];
}

export interface AdminTradeItem {
  id: string;           // UUID (for edit/remove endpoints)
  name: string;
  set: string;
  setCode: string;
  scryfallId: string;
  quantity: number;
  condition: string;
  language: string;
  foil: boolean;
  /** Authoritative finish when the source carries one; `foil` alone loses ETCHED. */
  finish?: HaveFinish | null;
  pointValue: number;
  completedAt?: string | null;  // set when the card has been individually completed (sender credited)
}

export interface AdminRemoveCardRequest {
  tradeId: string;
  itemId: string;       // UUID
}

export interface AdminCompleteCardRequest {
  tradeId: string;
  itemId: string;       // UUID
}

export interface CompleteCardInTradeResponse {
  success: boolean;
  message?: string;
  updatedTrade?: AdminTrade;
  creditedPoints?: number;  // net points credited to the sender for this card
}

export interface AdminTradesRequest {
  page?: number;
  pageSize?: number;
  search?: string;
  status?: 'ready' | 'transit' | 'completed' | 'cancelled';
  // Server-side filters, AND-combined; partial case-insensitive matches.
  sender?: string;       // sender username
  receiver?: string;     // receiver username
  tradeNumber?: string;  // Trade.tradeNumber (YY-NNN); backend strips a leading '#'
  cardName?: string;     // trades containing a matching non-removed card
}

export interface AdminTradesResponse {
  trades: AdminTrade[];
  totalCount: number;
  page: number;
  pageSize: number;
}

export interface InboundTradeCountResponse {
  count: number;
  hasOverdueTrades: boolean;
}

// Edit Trade Item interfaces
export interface EditableTradeItemPreferences {
  acceptableSetDetails: SetDetail[];
  acceptableConditions: string[];
  acceptableLanguages: string[];
  acceptFoil: boolean;
  acceptNonFoil: boolean;
}

export interface EditableTradeItem {
  itemIndex: number;
  itemId: string;              // TradeItem UUID
  cardId: number;
  cardName: string;
  quantity: number;
  currentCondition: string;
  currentLanguage: string;
  currentFoil: boolean;
  /** Track D; absent on a pre-D backend (derive from currentFoil) */
  currentFinish?: HaveFinish | null;
  currentEdition: string;
  currentPointValue: number;
  wantListId: number | null;
  preferences: EditableTradeItemPreferences | null;  // null when want entry deleted
}

export interface TradeItemEditOptionsResponse {
  items: EditableTradeItem[];
}

export interface UpdateTradeItemRequest {
  tradeId: string;
  itemId: string;              // TradeItem UUID
  cardId: number;
  condition: string;
  language: string;
  finish?: HaveFinish;
  foil: boolean;
}

export interface UpdateTradeItemResponse {
  success: boolean;
  message?: string;
  updatedTrade?: ActiveTrade;
  oldPointValue: number;
  newPointValue: number;
  perCardDelta: number;
  totalDelta: number;
  quantity: number;
  receiverNewBalance?: number;
  receiverBalance?: number;      // Present on error
  requiredIncrease?: number;     // Present on error
}

// Add Cards to Trade interfaces
export interface AddCardsToTradeRequest {
  tradeId: string;
  cards: {
    wantListId: number;
    cardId?: string;
    quantity: number;
    condition: string;
    language: string;
    finish?: HaveFinish;
    foil: boolean;
    edition?: string;
    set?: string;
  }[];
}

export interface AddCardsToTradeResponse {
  success: boolean;
  message?: string;
  tradeId: string;
  tradeNumber: string;
  addedCards: number;
  additionalValue: number;
  newTotalValue: number;
  receiverNewBalance: number;
}

// Auto-Match feature interfaces
export interface MatchIndexPackage {
  matchingCardIds: string[];  // Format: "{wantListId}-{haveId}"
  matchDetails?: Record<string, { condition: string; foil: boolean; finish?: HaveFinish; cardId: number }>;  // Key: composite "{wantListId}-{haveId}"; finish beside foil since Track D
  matchQuantityByWantId?: Record<string, number>;  // Sender's matching Have qty summed per want.id (stringified)
  // Physical cards the sender can fulfill for this package — deduplicated
  // across Want records. Use this for "X of Y cards match" display;
  // matchingCardIds.length over-counts because one Have matching N Wants
  // produces N compositeIds.
  matchedCardCount?: number;
  totalCards: number;
}

export interface MatchIndexResponse {
  packages: Record<string, MatchIndexPackage>;
  senderHaveQuantityByKey?: Record<string, number>;  // `${oracleId}|nonfoil` / `|foil` / `|etched` → sender's owned qty (no client consumer)
  totalMatchingCards: number;
  totalMatchingPackages: number;
  generatedAt: string;
  error?: string;        // "DB_ERROR" | "TIMEOUT" when failed
  errorMessage?: string; // Human-readable error
}

export const tradeApi = createApi({
  reducerPath: 'tradeApi',
  baseQuery: baseQuery,
  tagTypes: ['TradePackages', 'ActiveTrades', 'InboundTradeCount', 'MatchIndex'],
  endpoints: (builder) => ({
    getAvailablePackages: builder.query<AvailablePackagesResponse, AvailablePackagesRequest>({
      query: ({ page = 1, limit = 20, search, searchType, priceMin, priceMax, forExport, autoMatch, sort, direction, setCodes }) => {
        let url = `/trades/available-packages?page=${page}&limit=${limit}`;
        if (search) url += `&search=${encodeURIComponent(search)}`;
        if (searchType) url += `&searchType=${searchType}`;
        if (priceMin !== undefined && priceMin > 0) url += `&priceMin=${priceMin}`;
        if (priceMax !== undefined && priceMax > 0) url += `&priceMax=${priceMax}`;
        if (forExport) url += '&forExport=true';
        if (autoMatch) url += '&autoMatch=true';
        if (sort) url += `&sort=${sort}`;
        if (direction) url += `&direction=${direction}`;
        if (setCodes) url += `&setCodes=${encodeURIComponent(setCodes)}`;
        return {
          url,
          method: 'GET',
          requiresAuth: true,
        };
      },
      providesTags: ['TradePackages'],
    }),
    
    initiateTrade: builder.mutation<InitiateTradeResponse, InitiateTradeRequest>({
      query: (body) => ({
        url: '/trades/initiate',
        method: 'POST',
        body,
        requiresAuth: true,
      }),
      invalidatesTags: ['TradePackages', 'ActiveTrades'],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          // Invalidate onboarding checklist cache after initiating trade
          dispatch(onboardingApi.util.invalidateTags(['OnboardingChecklist']));
        } catch {
          // Ignore errors - the mutation itself will handle error states
        }
      },
    }),
    
    getActiveTrades: builder.query<ActiveTradesResponse, ActiveTradesRequest>({
      query: ({ type, status, page, limit, search }) => {
        const params = new URLSearchParams({
          type,
          status,
          page: page.toString(),
          limit: limit.toString(),
        });
        if (search && search.trim()) {
          params.append('search', search.trim());
        }
        return {
          url: `/trades/active?${params.toString()}`,
          method: 'GET',
          requiresAuth: true,
        };
      },
      providesTags: ['ActiveTrades'],
    }),

    // Panel data for /sending and /receiving. A separate request from
    // getActiveTrades because it is set-scoped: keyed on type + search only, so
    // tab switches and page changes reuse the cached entry.
    getActiveTradesPanel: builder.query<ActiveTradesPanelResponse, ActiveTradesPanelRequest>({
      query: ({ type, search }) => {
        const params = new URLSearchParams({ type });
        if (search && search.trim()) {
          params.append('search', search.trim());
        }
        return {
          url: `/trades/active/panel?${params.toString()}`,
          method: 'GET',
          requiresAuth: true,
        };
      },
      providesTags: ['ActiveTrades'],
    }),

    createTradeLock: builder.mutation<TradeLockResponse, TradeLockRequest>({
      query: ({ receiverId }) => ({
        url: `/trades/packages/${receiverId}/lock`,
        method: 'POST',
        body: {}, // Empty body - senderId is determined from auth token
        requiresAuth: true,
      }),
      invalidatesTags: ['TradePackages'],
    }),
    
    releaseTradeLock: builder.mutation<{ success: boolean }, TradeUnlockRequest>({
      query: ({ receiverId, lockToken }) => ({
        url: `/trades/packages/${receiverId}/lock`,
        method: 'DELETE',
        headers: {
          'X-Lock-Token': lockToken,  // Pass lock token as header
        },
        requiresAuth: true,
      }),
      invalidatesTags: ['TradePackages'],
    }),
    
    markAsShipped: builder.mutation<MarkAsShippedResponse, MarkAsShippedRequest>({
      query: (body) => ({
        url: '/trades/mark-shipped',
        method: 'POST',
        body,
        requiresAuth: true,
      }),
      invalidatesTags: ['ActiveTrades'],
    }),
    
    removeCardFromTrade: builder.mutation<RemoveCardFromTradeResponse, RemoveCardFromTradeRequest>({
      query: (body) => ({
        url: '/trades/remove-card',
        method: 'POST',
        body,
        requiresAuth: true,
      }),
      invalidatesTags: ['ActiveTrades', 'InboundTradeCount'],
    }),
    
    completeTrade: builder.mutation<CompleteTradeResponse, CompleteTradeRequest>({
      query: (body) => ({
        url: '/trades/complete',
        method: 'POST',
        body,
        requiresAuth: true,
      }),
      invalidatesTags: ['ActiveTrades', 'InboundTradeCount'],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          // Invalidate onboarding checklist cache after completing trade
          dispatch(onboardingApi.util.invalidateTags(['OnboardingChecklist']));
        } catch {
          // Ignore errors - the mutation itself will handle error states
        }
      },
    }),
    
    generateShareImage: builder.mutation<GenerateShareImageResponse, GenerateShareImageRequest>({
      query: ({ tradeId }) => ({
        url: `/trades/${tradeId}/share-image`,
        method: 'POST',
        requiresAuth: true,
      }),
    }),

    // Admin endpoint for getting all trades
    getAdminTrades: builder.query<AdminTradesResponse, AdminTradesRequest>({
      query: ({ page = 1, pageSize = 50, search, status, sender, receiver, tradeNumber, cardName }) => {
        // URLSearchParams encodes values itself — never pre-encode (PR #263 double-encoding bug)
        const params = new URLSearchParams();
        params.append('page', page.toString());
        params.append('pageSize', pageSize.toString());
        if (search) params.append('search', search);
        if (status) params.append('status', status);
        if (sender?.trim()) params.append('sender', sender.trim());
        if (receiver?.trim()) params.append('receiver', receiver.trim());
        if (tradeNumber?.trim()) params.append('tradeNumber', tradeNumber.trim());
        if (cardName?.trim()) params.append('cardName', cardName.trim());

        return {
          url: `/admin/trades?${params.toString()}`,
          method: 'GET',
          requiresAuth: true,
        };
      },
      providesTags: ['ActiveTrades'],
    }),

    getInboundTradeCount: builder.query<InboundTradeCountResponse, void>({
      query: () => ({
        url: '/trades/inbound-count',
        method: 'GET',
        requiresAuth: true,
      }),
      providesTags: ['InboundTradeCount'],
    }),

    // Auto-Match feature: Get match index for filtering packages
    getMatchIndex: builder.query<MatchIndexResponse, void>({
      query: () => ({
        url: '/trades/sendcards/match-index',
        method: 'GET',
        requiresAuth: true,
      }),
      keepUnusedDataFor: 120,  // 2 minutes - matches backend cache TTL
      providesTags: ['MatchIndex'],
    }),

    // Edit Trade Item: Fetch edit options (want list preferences) for a trade's items
    getTradeItemEditOptions: builder.query<TradeItemEditOptionsResponse, { tradeId: string }>({
      query: ({ tradeId }) => ({
        url: `/trades/${tradeId}/edit-options`,
        method: 'GET',
        requiresAuth: true,
      }),
      keepUnusedDataFor: 30,
    }),

    // Edit Trade Item: Update a trade item's attributes
    updateTradeItem: builder.mutation<UpdateTradeItemResponse, UpdateTradeItemRequest>({
      query: ({ tradeId, itemId, ...body }) => ({
        url: `/trades/${tradeId}/items/${itemId}`,
        method: 'PUT',
        body,
        requiresAuth: true,
      }),
      invalidatesTags: ['ActiveTrades'],
    }),

    // Add cards to an existing Ready-to-Ship trade
    addCardsToTrade: builder.mutation<AddCardsToTradeResponse, AddCardsToTradeRequest>({
      query: ({ tradeId, cards }) => ({
        url: `/trades/${tradeId}/add-cards`,
        method: 'POST',
        body: { cards },
        requiresAuth: true,
      }),
      invalidatesTags: ['ActiveTrades', 'InboundTradeCount'],
    }),

    // Admin: Get edit options for a trade's items (bypasses status/ownership guards)
    getAdminTradeEditOptions: builder.query<TradeItemEditOptionsResponse, { tradeId: string }>({
      query: ({ tradeId }) => ({
        url: `/admin/trades/${tradeId}/edit-options`,
        method: 'GET',
        requiresAuth: true,
      }),
      keepUnusedDataFor: 30,
    }),

    // Admin: Update a trade item (bypasses status/ownership guards)
    adminUpdateTradeItem: builder.mutation<UpdateTradeItemResponse, UpdateTradeItemRequest>({
      query: ({ tradeId, itemId, ...body }) => ({
        url: `/admin/trades/${tradeId}/items/${itemId}`,
        method: 'PUT',
        body,
        requiresAuth: true,
      }),
      invalidatesTags: ['ActiveTrades'],
    }),

    // Admin: Remove a card from a trade (bypasses status/ownership guards, uses itemId not itemIndex)
    adminRemoveCardFromTrade: builder.mutation<RemoveCardFromTradeResponse, AdminRemoveCardRequest>({
      query: (body) => ({
        url: '/admin/trades/remove-card',
        method: 'POST',
        body,
        requiresAuth: true,
      }),
      invalidatesTags: ['ActiveTrades'],
    }),

    // Admin: Complete a single card in a trade (mirror of remove-card; credits the sender, sets completedAt)
    adminCompleteCardInTrade: builder.mutation<CompleteCardInTradeResponse, AdminCompleteCardRequest>({
      query: (body) => ({
        url: '/admin/trades/complete-card',
        method: 'POST',
        body,
        requiresAuth: true,
      }),
      invalidatesTags: ['ActiveTrades'],
    }),

    // CSV Export: Track export analytics (fire-and-forget)
    trackCsvExport: builder.mutation<void, void>({
      query: () => ({
        url: '/trades/export-analytics',
        method: 'POST',
        requiresAuth: true,
      }),
    }),
  }),
});

export const {
  useGetAvailablePackagesQuery,
  useLazyGetAvailablePackagesQuery,
  useInitiateTradeMutation,
  useGetActiveTradesQuery,
  useGetActiveTradesPanelQuery,
  useCreateTradeLockMutation,
  useReleaseTradeLockMutation,
  useMarkAsShippedMutation,
  useRemoveCardFromTradeMutation,
  useCompleteTradeMutation,
  useGenerateShareImageMutation,
  useGetAdminTradesQuery,
  useGetInboundTradeCountQuery,
  useGetMatchIndexQuery,
  useLazyGetTradeItemEditOptionsQuery,
  useUpdateTradeItemMutation,
  useAddCardsToTradeMutation,
  useTrackCsvExportMutation,
  useLazyGetAdminTradeEditOptionsQuery,
  useAdminUpdateTradeItemMutation,
  useAdminRemoveCardFromTradeMutation,
  useAdminCompleteCardInTradeMutation,
} = tradeApi;